import { positionChecker } from 'src/util/useModelDesign'
import { DocumentContentGenerator, I18NText, Position, State, StateObj } from 'src/types'

const genGloveColorCell = (head: string, stateObj: StateObj, partsKey: string) => ({
  head,
  label: stateObj?.label,
  color: stateObj?.color,
  value: stateObj?.value,
  partsKey
})

const variablePartsGenerator = (state: State, position: Position, t: I18NText) => {
  const baseModelBackStyle = state.baseModel.backStyle
  const isLeatherIntegratedMiddle = state?.leatherIntegratedMiddle?.value === 'atMiddleFinger'
  const isUBack = state.baseModel.isUBack
  const isLoopOfRingFinger = state?.loopOfRingFinger?.value === 'loopOfRingFinger'
  const isNotFirstBack = baseModelBackStyle !== 'firstBackStyle'
  const isBelt = state?.beltType?.value !== 'thumbIntegrated' && !isNotFirstBack
  const isSUN01W = state.baseModel.productNumber === 'SUN-01W'

  switch (position) {
    case 'pitcher':
      switch (baseModelBackStyle) {
        case 'firstBackStyle':
          const isNormal = !isLeatherIntegratedMiddle
          return [
            genGloveColorCell(t.THUMB1, state.thumbOut, 'thumbOut'),
            isLeatherIntegratedMiddle && genGloveColorCell(t.THUMB2_INDEX_MIDDLE_RING7, state.thumbIndexMiddleRingRight, 'thumbIndexMiddleRingRight'),
            isNormal && genGloveColorCell(t.THUMB2_INDEX_MIDDLE5, state.thumbIndexMiddleRight, 'thumbIndexMiddleRight'),
            isNormal && genGloveColorCell(t.MIDDLE6_RING7, state.middleLeftRingRight, 'middleLeftRingRight'),
            genGloveColorCell(t.RING8_LITTLE9, state.ringLeftLittleRight, 'ringLeftLittleRight'),
            genGloveColorCell(t.LITTLE10, state.littleOut, 'littleOut'),
            genGloveColorCell(t.BELT, state.listBelt, 'listBelt'), // ベルト必須
            isLoopOfRingFinger && genGloveColorCell(t.LOOP_OF_RING, state.loopOfRingFingerColor, 'loopOfRingFingerColor'),
            genGloveColorCell('投手デザイン', state.pitcherDesign, 'pitcherDesign')
          ].filter(Boolean)
        case 'crownBackStyle':
          // クラウンバックは中指一体仕様不可
          return [
            genGloveColorCell(t.THUMB1, state.thumbOut, 'thumbOut'),
            genGloveColorCell(t.THUMB2, state.thumbWeb, 'thumbWeb'),
            genGloveColorCell(t.INDEX3, state.indexWeb, 'indexWeb'),
            genGloveColorCell(t.INDEX4_MIDDLE5, state.indexLeftMiddleRight, 'indexLeftMiddleRight'),
            genGloveColorCell(t.MIDDLE6_RING7, state.middleLeftRingRight, 'middleLeftRingRight'),
            genGloveColorCell(t.RING8_LITTLE9, state.ringLeftLittleRight, 'ringLeftLittleRight'),
            genGloveColorCell(t.LITTLE10, state.littleOut, 'littleOut'),
            isBelt && genGloveColorCell(t.BAND, state.listBelt, 'listBelt'),
            isLoopOfRingFinger && genGloveColorCell(t.LOOP_OF_RING, state.loopOfRingFingerColor, 'loopOfRingFingerColor'),
            genGloveColorCell('投手デザイン', state.pitcherDesign, 'pitcherDesign'),
            genGloveColorCell('ムートン', state.mouton, 'mouton')
          ].filter(Boolean)
        default:
          // case 'beltBackStyle':
          // ベルト一体型を考慮＿済
          return [
            genGloveColorCell(t.THUMB1, state.thumbOut, 'thumbOut'),
            genGloveColorCell(t.THUMB2, state.thumbWeb, 'thumbWeb'),
            genGloveColorCell(t.INDEX3, state.indexWeb, 'indexWeb'),
            genGloveColorCell(t.INDEX4, state.indexMiddle, 'indexMiddle'),
            genGloveColorCell(t.MIDDLE5, state.middleIndex, 'middleIndex'),
            !isLeatherIntegratedMiddle && genGloveColorCell(t.MIDDLE6, state.middleRing, 'middleRing'),
            genGloveColorCell(t.RING7, state.ringMiddle, 'ringMiddle'),
            genGloveColorCell(t.RING8, state.ringLittle, 'ringLittle'),
            genGloveColorCell(t.LITTLE9, state.littleRing, 'littleRing'),
            !isSUN01W && genGloveColorCell(t.LITTLE10, state.littleOut, 'littleOut'),
            isBelt && genGloveColorCell('ベルト', state.listBelt, 'listBelt'),
            isLoopOfRingFinger && genGloveColorCell(t.LOOP_OF_RING, state.loopOfRingFingerColor, 'loopOfRingFingerColor'),
            genGloveColorCell('投手デザイン', state.pitcherDesign, 'pitcherDesign'),
            genGloveColorCell('ムートン', state.mouton, 'mouton')
          ].filter(Boolean)
      }
    case 'infielder':
      const isNormalTurnAround = !isUBack
      return [
        genGloveColorCell(t.THUMB1, state.thumbOut, 'thumbOut'),
        isUBack && genGloveColorCell(t.THUMB2_INDEX3, state.thumbWebAndIndexWeb, 'thumbWebAndIndexWeb'),
        isNormalTurnAround && genGloveColorCell(t.THUMB2, state.thumbWeb, 'thumbWeb'),
        isNormalTurnAround && genGloveColorCell(t.INDEX3, state.indexWeb, 'indexWeb'),
        genGloveColorCell(t.INDEX4, state.indexMiddle, 'indexMiddle'),
        genGloveColorCell(`${t.MIDDLE}${isLeatherIntegratedMiddle ? '(一体)' : '5'}`, state.middleIndex, 'middleIndex'),
        !isLeatherIntegratedMiddle && genGloveColorCell(t.MIDDLE6, state.middleRing, 'middleRing'),
        genGloveColorCell(t.RING7, state.ringMiddle, 'ringMiddle'),
        genGloveColorCell(t.RING8, state.ringLittle, 'ringLittle'),
        genGloveColorCell(t.LITTLE9, state.littleRing, 'littleRing'),
        genGloveColorCell(t.LITTLE10, state.littleOut, 'littleOut'),
        isBelt && genGloveColorCell(t.BAND, state.listBelt, 'listBelt'),
        genGloveColorCell('ムートン', state.mouton, 'mouton'),
        isLoopOfRingFinger && genGloveColorCell(t.LOOP_OF_RING, state.loopOfRingFingerColor, 'loopOfRingFingerColor')
      ].filter(Boolean)
    case 'outfielder':
      // ベルト一体型を考慮
      return [
        genGloveColorCell(t.THUMB1, state.thumbOut, 'thumbOut'),
        genGloveColorCell(t.THUMB2, state.thumbWeb, 'thumbWeb'),
        genGloveColorCell(t.INDEX3, state.indexWeb, 'indexWeb'),
        genGloveColorCell(t.INDEX4, state.indexMiddle, 'indexMiddle'),
        genGloveColorCell(t.MIDDLE5, state.middleIndex, 'middleIndex'),
        !isLeatherIntegratedMiddle && genGloveColorCell(t.MIDDLE6, state.middleRing, 'middleRing'),
        genGloveColorCell(t.RING7, state.ringMiddle, 'ringMiddle'),
        genGloveColorCell(t.RING8, state.ringLittle, 'ringLittle'),
        genGloveColorCell(t.LITTLE9, state.littleRing, 'littleRing'),
        genGloveColorCell(t.LITTLE10, state.littleOut, 'littleOut'),
        isBelt && genGloveColorCell(t.BAND, state.listBelt, 'listBelt'),
        genGloveColorCell('ムートン', state.mouton, 'mouton'),
        isLoopOfRingFinger && genGloveColorCell(t.LOOP_OF_RING, state.loopOfRingFingerColor, 'loopOfRingFingerColor')
      ].filter(Boolean)
    default:
      return []
  }
}

export const genRagDeLionBaseCells = (state: State, position: Position) => {
  const genGloveBaseCell = (head: string, stateObj: StateObj) => ({ head, label: stateObj?.label, value: stateObj?.value })
  const { isPitcher, isCatcher, isGlove } = positionChecker(position)
  const baseModelStateObj = {
    label: state.baseModel.productNumber,
    value: state.baseModel.productNumber
  }
  const isMon01 = state.baseModel.productNumber === 'MON-01'
  const isSelectableMaterialPack = ['SUN-01', 'SUN-01C', 'SUN-01F', 'SUN-01L', 'SUN-01W'].includes(state.baseModel.productNumber)
  const isSUN01FLProduct = ['SUN-01F', 'SUN-01F'].includes(state.baseModel.productNumber)
  const isSUN01L = state.baseModel.productNumber === 'SUN-01L'
  const isSUN01C = state.baseModel.productNumber === 'SUN-01C'
  const isFullOrder = state.series?.value === 'fullOrder'
  const existsFingerGuard = !['none', 'unselected'].includes(state.fingerGuardType?.value)
  const existsLoopOfRingFinger = !['none', 'unselected'].includes(state.loopOfRingFinger?.value)
  const loopOfRingSize = state.loopOfRingFingerSize
  const loopOfRingFingerSizeObject = { label: loopOfRingSize.label + 'cm', value: loopOfRingSize.value + 'cm' }
  return [
    genGloveBaseCell('基本モデル', baseModelStateObj),
    genGloveBaseCell('シリーズ', state.series),
    genGloveBaseCell('利き腕', state.dominantArm),
    isSelectableMaterialPack && genGloveBaseCell('トジ部', state.materialPack),
    isGlove && genGloveBaseCell('ウェブパーツ', state.webParts),
    genGloveBaseCell('芯タイプ', state.materialType),
    isGlove && genGloveBaseCell('土手紐通し', state.bankLaceDirection),
    isGlove && genGloveBaseCell('背面ひも設定', state.backLace),
    isGlove && !isSUN01C && genGloveBaseCell('中指一体仕様', state.leatherIntegratedMiddle),
    genGloveBaseCell('グリス', state.greaseType),
    !isCatcher && genGloveBaseCell('薬指リング', state.loopOfRingFinger),
    existsLoopOfRingFinger && genGloveBaseCell('薬指リングサイズ', loopOfRingFingerSizeObject),
    !isCatcher && !isPitcher && genGloveBaseCell('ベルトタイプ', state.beltType),
    !isSUN01L && genGloveBaseCell('指カバー・指あて', state.fingerGuardType),
    existsFingerGuard && !isCatcher && !isSUN01FLProduct && genGloveBaseCell('指カバー位置', state.fingerGuardPosition),
    isGlove && isFullOrder && genGloveBaseCell('指の長さ変更', state.size),
    !isMon01 && !isPitcher && genGloveBaseCell('ラベルの位置', state.labelPosition)
  ].filter(Boolean)
}

export const checkRagDeLionWeb2 = (webPartsValue: string): boolean => {
  return ['type15'].includes(webPartsValue)
}
export const getRagDeLionGloveColorCells = (state: State, position: Position, t: I18NText) => {
  const productNumber = state.baseModel.productNumber
  const isSUN01L = productNumber === 'SUN-01L'

  const needFingerCoverOrPad = state.fingerGuardType?.value !== 'none' && !isSUN01L
  const needWeb2 = checkRagDeLionWeb2(state?.webParts?.value)

  return [
    genGloveColorCell(t.CATCH_SURFACE, state.catchFace, 'catchFace'),
    genGloveColorCell(t.WEB, state.web, 'web'),
    needWeb2 && genGloveColorCell(t.WEB2, state.web2, 'web2'),
    genGloveColorCell(t.THUMB_HOOK, state.thumbHook, 'thumbHook'),
    genGloveColorCell(t.LITTLE_HOOK, state.littleHook, 'littleHook'),
    needFingerCoverOrPad && genGloveColorCell(t.FINGER_GUARD, state.indexCover, 'indexCover'),
    genGloveColorCell(t.LININGS, state.linings, 'linings'),
    ...variablePartsGenerator(state, position, t),
    genGloveColorCell(t.EDGE, state.edge, 'edge'),
    genGloveColorCell(t.HAMIDASHI, state.hamidashi, 'hamidashi'),
    genGloveColorCell(t.LACE, state.lace, 'lace'),
    genGloveColorCell(t.STITCH, state.stitch, 'stitch'),
    genGloveColorCell('ラベル', state.ragLabel, 'ragLabel')
  ].filter(Boolean)
}

export const getRagDeLionFirstMittColorCells = (state: State, t: I18NText) => {
  const genFirstMittColorCell = (head: string, stateObj: StateObj, partsKey: string) => ({
    head,
    label: stateObj?.label,
    color: stateObj?.color,
    value: stateObj?.value,
    partsKey
  })

  const isBelt = state?.beltType?.value !== 'thumbIntegrated'
  const isLoopOfRingFinger = state?.loopOfRingFinger?.value === 'loopOfRingFinger'
  const needFingerCoverOrPad = state.fingerGuardType.value !== 'none'

  return [
    genFirstMittColorCell(t.CATCH_SURFACE, state.catchFace, 'catchFace'),
    genFirstMittColorCell(t.WEB, state.web, 'web'),
    genFirstMittColorCell(t.THUMB_HOOK, state.thumbHook, 'thumbHook'),
    genFirstMittColorCell(t.LITTLE_HOOK, state.littleHook, 'littleHook'),
    needFingerCoverOrPad && genFirstMittColorCell(t.FINGER_GUARD, state.indexCover, 'indexCover'),
    genFirstMittColorCell(t.LININGS, state.linings, 'linings'),
    genFirstMittColorCell(t.THUMB, state.thumb, 'thumb'),
    genFirstMittColorCell(t.UNDER_WEB, state.underWeb, 'underWeb'),
    genFirstMittColorCell(t.BOOMERANG, state.boomerang, 'boomerang'),
    genFirstMittColorCell(t.EDGE, state.edge, 'edge'),
    isBelt && genFirstMittColorCell(t.BAND, state.listBelt, 'listBelt'),
    isLoopOfRingFinger && genFirstMittColorCell(t.LOOP_OF_RING, state.loopOfRingFingerColor, 'loopOfRingFingerColor'),
    genFirstMittColorCell(t.LACE, state.lace, 'lace'),
    genFirstMittColorCell(t.STITCH, state.stitch, 'stitch'),
    genFirstMittColorCell('ムートン', state.mouton, 'mouton'),
    genFirstMittColorCell('ラベル', state.ragLabel, 'ragLabel')
  ].filter(Boolean)
}

export const getRagDeLionCatcherMittColorCells = (state: State, t: I18NText) => {
  const genCatcherMittColorCell = (head: string, stateObj: StateObj, partsKey: string) => ({
    head,
    label: stateObj?.label,
    color: stateObj?.color || 'No data',
    value: stateObj?.value,
    partsKey
  })

  const needFingerCoverOrPad = state.fingerGuardType.value !== 'none'
  const backStyleValue = state.baseModel?.backStyle
  const isRegularBackStyle = backStyleValue === 'regularBackStyle'
  const isBeltBackStyle = backStyleValue === 'beltBackStyle'
  const isThumbIntegrated = ['thumbIntegrated'].includes(state.beltType?.value)
  return [
    genCatcherMittColorCell(t.CATCH_SURFACE, state.catchFace, 'catchFace'),
    genCatcherMittColorCell(t.LACE, state.lace, 'lace'),
    genCatcherMittColorCell(t.WEB, state.web, 'web'),
    genCatcherMittColorCell(t.EDGE, state.edge, 'edge'),
    genCatcherMittColorCell(t.STITCH, state.stitch, 'stitch'),
    genCatcherMittColorCell(t.THUMB_HOOK, state.thumbHook, 'thumbHook'),
    genCatcherMittColorCell(t.LITTLE_HOOK, state.littleHook, 'littleHook'),
    needFingerCoverOrPad && genCatcherMittColorCell(t.FINGER_GUARD, state.indexCover, 'indexCover'),
    genCatcherMittColorCell(t.LININGS, state.linings, 'linings'),
    genCatcherMittColorCell(t.STAND, state.fingerStand, 'fingerStand'),
    genCatcherMittColorCell('親指マチ', state.thumbMachi, 'thumbMachi'),
    genCatcherMittColorCell('小指マチ', state.littleMachi, 'littleMachi'),
    genCatcherMittColorCell(t.THUMB_INDEX_MIDDLE5, state.thumbIndexMiddleRight, 'thumbIndexMiddleRight'),
    isRegularBackStyle && genCatcherMittColorCell(t.MIDDLE5_RING, state.middleLeftRing, 'middleLeftRing'),
    isRegularBackStyle && genCatcherMittColorCell(t.LITTLE10, state.littleOut, 'littleOut'),
    isRegularBackStyle && genCatcherMittColorCell(t.FINGER_TIP_OUT, state.indexFingerTipOut, 'indexFingerTipOut'),
    isBeltBackStyle && genCatcherMittColorCell(t.MIDDLE6_RING_PINKY, state.middleLeftRingLittle, 'middleLeftRingLittle'),
    isRegularBackStyle && !isThumbIntegrated && genCatcherMittColorCell(t.BELT, state.listBelt, 'listBelt'),
    genCatcherMittColorCell('ハミダシ(背面)', state.hamidashi, 'hamidashi'),
    genCatcherMittColorCell('ハミダシ(捕球面)', state.hamidashiOfCatchFace, 'hamidashiOfCatchFace'),
    state.baseModel.productNumber !== 'MON-01' && genCatcherMittColorCell('ムートン', state.mouton, 'mouton'),
    genCatcherMittColorCell('ラベル', state.ragLabel, 'ragLabel')
  ].filter(Boolean)
}

const genCell = (head: string, stateObj: StateObj) => ({ head, label: stateObj?.label, value: stateObj?.value })

export const getRagDeLionOptionalEmbroideryCells = (state: State) => {
  switch (state.ragLionLogo.value) {
    case 'none':
      return [genCell('ロゴ', state.ragLionLogo)]
    default:
      return [genCell('ロゴ', state.ragLionLogo), genGloveColorCell('カラー', state.ragLionLogoColor, 'ragLionLogoColor')].filter(Boolean)
  }
}

export const ragDeLionOriginalEmbroiderySettings = (state: State, genCellContent: DocumentContentGenerator): any => {
  const existsRagLionLogo = !['none', 'unselected'].includes(state.ragLionLogo.value)
  const ragLionLogoColorLabel = existsRagLionLogo ? state.ragLionLogoColor.label : '--'
  const ragLionLogoColorValue = existsRagLionLogo ? state.ragLionLogoColor.value : '#323232'

  const existsRagLogo = !['none', 'unselected'].includes(state.ragLogo.value)
  const ragLogoColorLabel = existsRagLogo ? state.ragLogoColor.label : '--'
  const ragLogoColorValue = existsRagLogo ? state.ragLogoColor.value : '#323232'

  return [
    {
      text: `ライオンロゴ刺繍設定`,
      style: { fontSize: 11 },
      margin: [0, 8, 0, 4]
    },
    {
      table: {
        widths: '50%',
        body: [[genCellContent(`タイプ`, state.ragLionLogo.label), genCellContent(`カラー`, ragLionLogoColorLabel, 'right', ragLionLogoColorValue)]]
      }
    },
    {
      text: `RAGロゴ刺繍設定`,
      style: { fontSize: 11 },
      margin: [0, 8, 0, 4]
    },
    {
      table: {
        widths: '50%',
        body: [[genCellContent(`タイプ`, state.ragLogo.label), genCellContent(`カラー`, ragLogoColorLabel, 'right', ragLogoColorValue)]]
      }
    }
  ]
}
